import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { UserCheck, Eye, EyeOff, Clock, CheckCircle, X, ExternalLink, Truck, PackageCheck, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import DQFChecklist from './DQFChecklist';
import DriverFormModal from './DriverFormModal';
import { numOrDefault } from '@/lib/invoiceHelpers';

const MaskedAccount = ({ value }) => {
  const [revealed, setRevealed] = useState(false);
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const masked = '•••• •••• ' + String(value).slice(-4);
  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-mono text-foreground">{revealed ? value : masked}</span>
      <button onClick={() => setRevealed(v => !v)} className="text-muted-foreground hover:text-primary transition-colors" title={revealed ? 'Hide' : 'Reveal'}>
        {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
      </button>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-border last:border-0">
    <span className="text-xs text-muted-foreground flex-shrink-0 w-36">{label}</span>
    <span className="text-xs text-foreground text-right">{value || '—'}</span>
  </div>
);

const DocLink = ({ label, url }) => {
  if (!url) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-border">
      <span className="text-xs text-muted-foreground flex-shrink-0 w-36">{label}</span>
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors">
        <ExternalLink className="w-3 h-3" /> View Upload
      </a>
    </div>
  );
};

export default function PendingApprovalsTab() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [approving, setApproving] = useState(null);
  // editing = the driver object currently being edited in DriverFormModal,
  // or null when no edit modal is open. Reuses the same modal the admin
  // panel uses for driver editing so all fields (CDL expiration, EIN,
  // truck info, VIN, etc.) are available to fill in -- the pending
  // approval detail view is otherwise read-only.
  const [editing, setEditing] = useState(null);
  const [dispatchFeePercent, setDispatchFeePercent] = useState(3);

  const isDqfReady = (driver) =>
    (driver.dqf_clearinghouse ?? false) && (driver.dqf_drug_test ?? false);

  useEffect(() => {
    loadPending();
    // Was hardcoded "3%" in the applicant detail view regardless of what
    // Accounting configured in Pay & Deductions.
    client.entities.CompanySettings.filter({ key: 'dispatch_fee_percent' })
      .then(settings => setDispatchFeePercent(numOrDefault(settings[0]?.value, 3)))
      .catch(() => {});
  }, []);

  const loadPending = async () => {
    setLoading(true);
    const data = await client.entities.DriverProfile.filter({ approval_status: 'Pending', onboarding_complete: true }, '-created_date', 100);
    setDrivers(data);
    setLoading(false);
  };

  // "2023 Utility 4000D-X" -> { year, make, model } (onboarding stores trailer as one combined string)
  const buildVehicle = (info) => {
    const tokens = String(info || '').trim().split(/\s+/).filter(Boolean);
    let year = '';
    if (/^(19|20)\d{2}$/.test(tokens[0] || '')) year = tokens.shift();
    return { year, make: tokens.shift() || '', model: tokens.join(' ') };
  };

  const unitNumber = (vin, prefix) => {
    const tail = String(vin || '').replace(/\s+/g, '').slice(-6).toUpperCase();
    return `${prefix}-${tail || Date.now().toString(36).toUpperCase()}`;
  };

  // Creates Truck (and Trailer, if provided) Asset records from an Owner
  // Operator's onboarding data (own AND drive -- links via
  // assigned_driver_email). Skips any that already exist so a re-approval
  // after re-onboarding doesn't duplicate.
  const createOwnerAssets = async (driver) => {
    const existing = await client.entities.Asset.list('-created_date', 200);
    const driverEmail = (driver.user_email || '').toLowerCase();
    // A real VIN identifies a specific physical vehicle -- only match on
    // VIN equality for it, never fall back to "this driver already has
    // *an* asset of this type" (that fallback previously applied
    // regardless of whether a VIN was provided, so a driver re-approved
    // with a genuinely different truck -- new VIN, different
    // make/model -- silently never got that truck added: it looked
    // identical to a true duplicate). The "any asset of this type"
    // fallback still applies, deliberately, only when there's no VIN at
    // all, to avoid creating a second blank-VIN placeholder asset on
    // every re-approval.
    const isDup = (vin, type) => existing.some(a =>
      vin ? (a.vin && a.vin === vin)
        : (driverEmail && (a.assigned_driver_email || '').toLowerCase() === driverEmail && a.asset_type === type)
    );
    const created = [];
    const ownerLink = { assigned_driver_email: driver.user_email || '' };
    const sourceLabel = 'Auto-added from Owner Operator onboarding.';

    const truck = {
      year: driver.truck_year || '',
      make: driver.truck_make || '',
      model: driver.truck_model || '',
    };
    // Pre-schema-fix records stored only the combined truck_info string
    if (!truck.year && !truck.make && !truck.model && driver.truck_info) {
      const p = buildVehicle(driver.truck_info);
      truck.year = p.year; truck.make = p.make; truck.model = p.model;
    }

    if ((driver.truck_vin || truck.make || driver.truck_info) && !isDup(driver.truck_vin, 'Truck')) {
      await client.entities.Asset.create({
        unit_number: unitNumber(driver.truck_vin, 'T'),
        asset_type: 'Truck',
        year: truck.year,
        make: truck.make,
        model: truck.model,
        vin: driver.truck_vin || '',
        license_plate: driver.license_plate || '',
        plate_state: driver.plate_state || '',
        plate_expiration: driver.plate_expiration || '',
        registration_expiration: driver.registration_expiration || '',
        annual_inspection_due: driver.annual_inspection_due || '',
        ownership_type: 'Owned',
        ...ownerLink,
        is_active: true,
        current_odometer: Number(driver.current_odometer) || 0,
        notes: [
          driver.truck_cab_type && `Cab: ${driver.truck_cab_type}`,
          driver.engine_specs && `Engine: ${driver.engine_specs}`,
          driver.truck_registration_url && `Registration: ${driver.truck_registration_url}`,
          driver.dot_inspection_url && `DOT Inspection: ${driver.dot_inspection_url}`,
          sourceLabel,
        ].filter(Boolean).join('\n'),
      });
      created.push('Truck');
    }

    // Explicit flag from onboarding; fall back to inferring for legacy records
    // created before has_trailer was persisted.
    const hasTrailer = driver.has_trailer ?? !!(driver.trailer_vin || driver.trailer_info);
    if (hasTrailer && (driver.trailer_vin || driver.trailer_info) && !isDup(driver.trailer_vin, 'Trailer')) {
      const v = buildVehicle(driver.trailer_info);
      await client.entities.Asset.create({
        unit_number: unitNumber(driver.trailer_vin, 'TR'),
        asset_type: 'Trailer',
        year: v.year,
        make: v.make,
        model: v.model,
        vin: driver.trailer_vin || '',
        ownership_type: 'Owned',
        ...ownerLink,
        is_active: true,
        current_odometer: 0,
        notes: [driver.trailer_info, sourceLabel].filter(Boolean).join('\n'),
      });
      created.push('Trailer');
    }
    return created;
  };

  const handleApprove = async (driver) => {
    setApproving(driver.id);
    await client.entities.DriverProfile.update(driver.id, {
      approval_status: 'Approved',
      is_active: true,
    });
    let assetNote = '';
    // Owner Operators get an asset only if they bring their own equipment
    // (not leasing from the carrier).
    const ownsEquipment =
      driver.driver_type === 'Owner Operator' && driver.equipment_preference !== 'lease';
    if (ownsEquipment) {
      try {
        const created = await createOwnerAssets(driver);
        if (created.length) assetNote = ` ${created.join(' & ')} added to Assets.`;
      } catch {
        toast.error(`${driver.full_name} approved, but auto-adding their equipment to Assets failed — add it manually in the Assets tab.`);
        setDrivers(prev => prev.filter(d => d.id !== driver.id));
        setApproving(null);
        setExpanded(null);
        return;
      }
    }
    toast.success(`${driver.full_name} has been approved!${assetNote}`);
    setDrivers(prev => prev.filter(d => d.id !== driver.id));
    setApproving(null);
    setExpanded(null);
  };

  const handleReject = async (driver) => {
    setApproving(driver.id);
    await client.entities.DriverProfile.update(driver.id, { approval_status: 'Rejected', is_active: false });
    toast.info(`${driver.full_name} application rejected.`);
    setDrivers(prev => prev.filter(d => d.id !== driver.id));
    setApproving(null);
    setExpanded(null);
  };

  if (loading) {
    return (
      <div className="glass-card p-12 flex justify-center">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const GROUPS = [
    { type: 'Company Driver', label: 'Company Drivers', color: 'text-blue-400' },
    { type: 'Owner Operator', label: 'Owner Operators', color: 'text-purple-400' },
    { type: '__other__', label: 'Uncategorized', color: 'text-muted-foreground' },
  ];

  const getGroup = (driver) => {
    if (driver.driver_type === 'Company Driver' || driver.driver_type === 'Owner Operator') return driver.driver_type;
    return '__other__';
  };

  if (drivers.length === 0) {
    return (
      <div className="glass-card p-12 text-center">
        <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
        <p className="text-foreground font-semibold">All clear!</p>
        <p className="text-muted-foreground text-sm mt-1">No pending driver applications.</p>
      </div>
    );
  }

  const renderDriverCard = (driver) => (
    <div key={driver.id} className="glass-card overflow-hidden">
      {/* Summary row */}
      <div className="flex items-center justify-between px-5 py-4 gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{driver.full_name}</p>
            <p className="text-xs text-muted-foreground">
              {driver.user_email}{driver.truck_type ? ` · ${driver.truck_type}` : ''}
            </p>
            {/* Company Name is Owner-Operator-only -- checked on
                driver_type too, not just truthiness, so a Company
                Driver applicant (already grouped under "Company
                Drivers" by GROUPS below) never shows a contradictory
                company-name subtitle right on this same review card. */}
            {driver.company_name && driver.driver_type !== 'Company Driver' && <p className="text-xs text-primary/80">{driver.company_name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setExpanded(expanded === driver.id ? null : driver.id)}
            className="text-xs text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors border border-border"
          >
            {expanded === driver.id ? 'Hide Details' : 'Review'}
          </button>
          {/* Edit opens DriverFormModal pre-filled with this driver's
              record. Lets the admin fill in fields the driver left
              blank (CDL expiration, EIN, truck VIN, license plate,
              etc.) without leaving the Pending Approvals queue. */}
          <button
            onClick={() => setEditing(driver)}
            className="text-xs text-muted-foreground hover:text-primary px-3 py-1.5 rounded-lg hover:bg-white/5 transition-colors border border-border inline-flex items-center gap-1"
            title="Edit driver profile -- fill in missing fields"
          >
            <Pencil className="w-3 h-3" /> Edit
          </button>
          <Button
            size="sm"
            onClick={() => handleApprove(driver)}
            disabled={approving === driver.id || !isDqfReady(driver)}
            title={!isDqfReady(driver) ? 'Clearinghouse & Drug Test required' : ''}
            className={cn(
              "h-8 px-3 border",
              isDqfReady(driver)
                ? "bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-500/30"
                : "bg-muted/40 text-muted-foreground border-border cursor-not-allowed"
            )}
          >
            <UserCheck className="w-3 h-3 mr-1" />
            {approving === driver.id ? '...' : 'Approve'}
          </Button>
          <Button
            size="sm"
            onClick={() => handleReject(driver)}
            disabled={approving === driver.id}
            className="h-8 px-3 border bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30"
          >
            <X className="w-3 h-3 mr-1" />
            {approving === driver.id ? '...' : 'Deny'}
          </Button>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded === driver.id && (
        <div className="px-5 pb-5 border-t border-border pt-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

            {/* Contact & Identity */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Contact & Identity</p>
              <DetailRow label="Phone" value={driver.phone} />
              <DetailRow label="Address" value={driver.address} />
              <DetailRow label="CDL Number" value={driver.cdl_number} />
              <DetailRow label="CDL Expiration" value={driver.cdl_expiration_date} />
              <DetailRow label="EIN / SSN" value={driver.ein} />
              <DocLink label="Driver's License" url={driver.dl_document_url} />
              <DocLink label="CDL Document" url={driver.cdl_document_url} />
            </div>

            {/* Payment */}
            <div>
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Payment</p>
              <DetailRow label="Routing Number" value={driver.bank_routing_number} />
              <div className="flex items-start justify-between gap-4 py-1.5 border-b border-border">
                <span className="text-xs text-muted-foreground flex-shrink-0 w-36">Account Number</span>
                <MaskedAccount value={driver.bank_account_number} />
              </div>
              <DocLink label="Void Check / DD Form" url={driver.void_check_url} />
            </div>

            {/* Equipment */}
            <div className="sm:col-span-2">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Equipment</p>
              {driver.equipment_preference && (
                <div className={cn(
                  "flex items-center gap-2 mb-3 px-3 py-2 rounded-lg border w-fit text-xs font-semibold",
                  driver.equipment_preference === 'lease' ? 'bg-primary/10 border-primary/30 text-primary' : 'bg-green-500/10 border-green-500/30 text-green-400'
                )}>
                  {driver.equipment_preference === 'lease'
                    ? <><Truck className="w-3.5 h-3.5" /> Leasing from GF Trucklines</>
                    : <><PackageCheck className="w-3.5 h-3.5" /> Bringing Own Equipment</>}
                </div>
              )}
              {driver.equipment_preference !== 'lease' && (
                <div className="grid grid-cols-2 gap-x-6">
                  <DetailRow label="Truck Type" value={driver.truck_type} />
                  <DetailRow label="Truck Info" value={driver.truck_info} />
                  <DetailRow label="Trailer" value={driver.trailer_info} />
                  <DetailRow label="VIN" value={driver.truck_vin} />
                  <DetailRow label="License Plate" value={driver.license_plate} />
                  <DocLink label="Truck Registration" url={driver.truck_registration_url} />
                  <DocLink label="DOT Annual Inspection" url={driver.dot_inspection_url} />
                </div>
              )}
            </div>

            {/* Dispatch */}
            <div className="sm:col-span-2 border-t border-border pt-3">
              <p className="text-xs font-semibold text-primary uppercase tracking-widest mb-2">Services</p>
              <DetailRow label="Dispatch Service" value={driver.dispatch_service ? `Yes (${dispatchFeePercent}% per load)` : 'No'} />
            </div>

            {/* DQF Checklist */}
            <div className="sm:col-span-2 border-t border-border pt-4">
              <DQFChecklist
                driver={driver}
                onSaved={(updates) => setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, ...updates } : d))}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{drivers.length} application{drivers.length !== 1 ? 's' : ''} awaiting review</p>
      {GROUPS.map(({ type, label, color }) => {
        const group = drivers.filter(d => getGroup(d) === type);
        if (group.length === 0) return null;
        return (
          <div key={type} className="space-y-3">
            <p className={cn("text-xs font-semibold uppercase tracking-widest px-1", color)}>{label} ({group.length})</p>
            {group.map(driver => renderDriverCard(driver))}
          </div>
        );
      })}

      {/* Driver edit modal -- mounted at the tab root so it floats over
          the approval list. onSaved merges the returned updates back
          into the local drivers state so the card refreshes inline
          (no full re-fetch round trip). */}
      {editing && (
        <DriverFormModal
          driver={editing}
          onClose={() => setEditing(null)}
          onSaved={(updates) => {
            if (updates) {
              setDrivers(prev => prev.map(d =>
                d.id === editing.id ? { ...d, ...updates } : d
              ));
            }
          }}
        />
      )}
    </div>
  );
}